import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { connect, useDispatch } from "react-redux";

import moment from "moment";

import { setUserAction } from "store/actions/userProfile";
import useFetchUserDetailsHandler from "hooks/useFetchUserDetailsHandler";

import { getToken } from "api/authentication";

import getLogger from "utils/logger.util";
import { extractCode } from "utils/utils";

import { setToken } from "../store/thunk/auth";

export const SSOContext = React.createContext();

export const useSSO = () => useContext(SSOContext);

const SSOProvider = ({ children, onSetToken, ...initOptions }) => {
  const Logger = getLogger("SSOProvider");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(true);
  let refreshInterval = null;

  const AUTH_ENDPOINT = `${process.env.REACT_APP_LOGIN_URL}/services/oauth2/authorize`;

  const dispatch = useDispatch();
  const { fetchUserDetails } = useFetchUserDetailsHandler(useDispatch());

  useEffect(() => {
    const initSSO = async () => {
      try {
        if (!window.location.search.includes("code=")) {
          const pkceResponse = await axios.get(`${process.env.REACT_APP_LOGIN_URL}/services/oauth2/pkce/generator`);
          const { code_challenge, code_verifier } = pkceResponse.data;
          sessionStorage.setItem("pkce_code_verifier", code_verifier);

          const redirectUrl =
            `${AUTH_ENDPOINT}?response_type=code` +
            `&client_id=${process.env.REACT_APP_CONSUMER_KEY}` +
            `&redirect_uri=${encodeURIComponent(initOptions.redirectUri)}` +
            `&code_challenge=${code_challenge}` +
            `&code_challenge_method=S256`;
          window.location.href = redirectUrl;
          return;
        }

        const codeVerifier = sessionStorage.getItem("pkce_code_verifier");

        const getTokenParam = {
          code: extractCode(),
          client_id: process.env.REACT_APP_CONSUMER_KEY,
          code_verifier: codeVerifier,
          redirect_uri: initOptions.redirectUri,
          grant_type: "authorization_code"
        };

        const refreshToken = await getAuthorization(getTokenParam);
        refreshTokenTimer(refreshToken);
      } catch (err) {
        Logger.error("initSSO - error: ", err);
      }
      setLoading(false);
      setRefresh(false);
    };
    initSSO();
  }, []);

  async function refreshToken(token) {
    const codeVerifier = sessionStorage.getItem("pkce_code_verifier");
    const getRefreshTokenParam = {
      client_id: process.env.REACT_APP_CONSUMER_KEY,
      refresh_token: token,
      grant_type: "refresh_token",
      code_verifier: codeVerifier
    };
    await getAuthorization(getRefreshTokenParam);
  }

  const refreshTokenTimer = refresh_token => {
    setInterval(() => {
      refreshToken(refresh_token);
    }, refreshInterval);
  };

  async function getAuthorization(getTokenParam) {
    try {
      const response = await getToken(getTokenParam);
      if (response) {
        const { refresh_token, id_token } = response;
        const decodedAccessToken = jwt_decode(id_token);
        const { custom_attributes, exp } = decodedAccessToken;
        const { UserEmail, Phone, UserId, CompanyName } = custom_attributes;
        dispatch(setUserAction({ UserEmail, Phone, UserId, CompanyName }));
        setIsAuthenticated(true);
        onSetToken(refresh_token, id_token, null, UserId);
        fetchUserDetails();

        const currentTime = moment().add(1, "minutes");
        const expireTime = moment.unix(exp);
        refreshInterval = expireTime.diff(currentTime, "milliseconds");
        return refresh_token;
      }
    } catch (err) {
      Logger.error("getAuthorization - error: ", err);
    }
  }

  return (
    <SSOContext.Provider
      value={{
        isAuthenticated,
        loading,
        refresh
      }}
    >
      {children}
    </SSOContext.Provider>
  );
};

const mapDispatchToProps = dispatch => ({
  onSetToken: (refreshToken, idToken, isAdmin, ssoId) => dispatch(setToken(refreshToken, idToken, isAdmin, ssoId))
});

export default connect(null, mapDispatchToProps)(SSOProvider);
